import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { authApi } from "@/client/features/auth/auth-api";
import { ApiError } from "@/client/lib/api";

type Status = "verifying" | "success" | "error";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");
  const [message, setMessage] = useState(
    token ? "" : "This verification link is missing or malformed.",
  );
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) {
      return;
    }
    started.current = true;
    authApi
      .verifyEmail({ token })
      .then(() => {
        setStatus("success");
        setMessage("Your email address has been verified.");
      })
      .catch((err: unknown) => {
        setStatus("error");
        setMessage(
          err instanceof ApiError
            ? err.message
            : "We couldn't verify your email. The link may have expired.",
        );
      });
  }, [token]);

  return (
    <Card>
      <CardHeader>
        <div className="bg-muted mb-1 flex size-10 items-center justify-center rounded-full">
          {status === "verifying" ? (
            <Loader2 className="size-5 animate-spin" />
          ) : status === "success" ? (
            <CheckCircle2 className="size-5" />
          ) : (
            <XCircle className="size-5" />
          )}
        </div>
        <CardTitle>
          {status === "verifying"
            ? "Verifying your email…"
            : status === "success"
              ? "Email verified"
              : "Verification failed"}
        </CardTitle>
        {message ? <CardDescription>{message}</CardDescription> : null}
      </CardHeader>
      {status !== "verifying" ? (
        <CardFooter>
          <Button asChild className="w-full">
            <Link to="/login">Continue to sign in</Link>
          </Button>
        </CardFooter>
      ) : (
        <CardContent />
      )}
    </Card>
  );
}
